/**
 * useLocalizedTestimonials - Maps US testimonial locations to locale-appropriate equivalents
 * 
 * When a visitor is on an international locale route (UK, AU, EU), this hook
 * replaces US city/state locations with culturally appropriate local cities,
 * and optionally adjusts reviewer names to feel more natural for that market.
 * 
 * US visitors see the original US locations unchanged.
 */
import { useLocale } from "@/contexts/LocaleContext";

// ============================================
// LOCALE-SPECIFIC LOCATION MAPPINGS
// ============================================

// UK locations - spread across England, Scotland, Wales, Northern Ireland
const UK_LOCATIONS: string[] = [
  "Manchester, UK",
  "Edinburgh, UK",
  "Bristol, UK",
  "Glasgow, UK",
  "Birmingham, UK",
  "Leeds, UK",
  "Liverpool, UK",
  "Cardiff, UK",
  "Brighton, UK",
  "Bath, UK",
  "Cambridge, UK",
  "Oxford, UK",
  "York, UK",
  "Belfast, UK",
  "Nottingham, UK",
  "Sheffield, UK",
];

// UK-appropriate names (distinctly British names)
const UK_NAMES: string[] = [
  "David & Linda",
  "Margaret T.",
  "Rupert K.",
  "Fiona C.",
  "Alistair H.",
  "Nigel P.",
  "Pippa W.",
  "Hamish L.",
  "Sarah M.",
  "Thomas R.",
  "Emma B.",
  "Gareth H.",
  "Charlotte D.",
  "Oliver S.",
  "Helen R.",
  "George W.",
];

// AU locations - spread across states
const AU_LOCATIONS: string[] = [
  "Melbourne, VIC",
  "Sydney, NSW",
  "Brisbane, QLD",
  "Perth, WA",
  "Adelaide, SA",
  "Gold Coast, QLD",
  "Canberra, ACT",
  "Hobart, TAS",
  "Newcastle, NSW",
  "Wollongong, NSW",
  "Geelong, VIC",
  "Cairns, QLD",
  "Townsville, QLD",
  "Darwin, NT",
  "Ballarat, VIC",
  "Sunshine Coast, QLD",
];

// AU-appropriate names (distinctly Australian names and nicknames)
const AU_NAMES: string[] = [
  "Bec & Lachlan",
  "Tash T.",
  "Macca K.",
  "Gemma C.",
  "Callum H.",
  "Davo P.",
  "Hayley W.",
  "Jono L.",
  "Sarah M.",
  "Thomas R.",
  "Karen B.",
  "Andrew H.",
  "Shazza D.",
  "Chris S.",
  "Donna R.",
  "Mark W.",
];

// EU locations - spread across major EU countries
const EU_LOCATIONS: string[] = [
  "Munich, Germany",
  "Amsterdam, Netherlands",
  "Paris, France",
  "Milan, Italy",
  "Barcelona, Spain",
  "Vienna, Austria",
  "Copenhagen, Denmark",
  "Stockholm, Sweden",
  "Dublin, Ireland",
  "Lisbon, Portugal",
  "Brussels, Belgium",
  "Helsinki, Finland",
  "Prague, Czech Republic",
  "Zurich, Switzerland",
  "Oslo, Norway",
  "Berlin, Germany",
];

// EU-appropriate names (distinctly European names matching country flags)
const EU_NAMES: string[] = [
  "Katharina & Florian",
  "Camille T.",
  "Joost K.",
  "Giulia C.",
  "Anneliese H.",
  "Alejandro P.",
  "Astrid W.",
  "Søren L.",
  "Siobhán M.",
  "Thomas R.",
  "Elena B.",
  "Lars H.",
  "Marie D.",
  "Stefan S.",
  "Claudia R.",
  "Henrik W.",
];

// ============================================
// MAPPING LOGIC
// ============================================

interface TestimonialInput {
  name: string;
  location: string;
  [key: string]: any; // Allow any other fields to pass through
}

interface LocalizedTestimonialResult<T extends TestimonialInput> {
  testimonials: T[];
}

/**
 * Simple hash function to get a deterministic index from a string
 * This ensures the same testimonial always maps to the same locale location
 */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Get locale-specific locations and names based on country code
 */
function getLocaleMapping(countryCode: string): { locations: string[]; names: string[] } | null {
  switch (countryCode) {
    case 'GB':
      return { locations: UK_LOCATIONS, names: UK_NAMES };
    case 'AU':
      return { locations: AU_LOCATIONS, names: AU_NAMES };
    case 'EU':
      return { locations: EU_LOCATIONS, names: EU_NAMES };
    default:
      return null; // US or unknown — keep original
  }
}

/**
 * Localize a single testimonial's location (and optionally name)
 * Uses deterministic hashing so the same testimonial always gets the same mapped location
 */
function localizeTestimonial<T extends TestimonialInput>(
  testimonial: T,
  index: number,
  mapping: { locations: string[]; names: string[] }
): T {
  // Use a combination of index and name hash for deterministic but varied mapping
  const locationIndex = (index + simpleHash(testimonial.name)) % mapping.locations.length;
  const nameIndex = index % mapping.names.length;
  
  return {
    ...testimonial,
    location: mapping.locations[locationIndex],
    name: mapping.names[nameIndex],
  };
}

/**
 * Hook: useLocalizedTestimonials
 * 
 * Takes an array of testimonials and returns locale-appropriate versions.
 * US visitors see original data. International visitors see mapped locations and names.
 * 
 * @example
 * const { testimonials } = useLocalizedTestimonials(originalTestimonials);
 */
export function useLocalizedTestimonials<T extends TestimonialInput>(
  originalTestimonials: T[]
): LocalizedTestimonialResult<T> {
  const { locale, isInternational } = useLocale();
  
  if (!isInternational) {
    return { testimonials: originalTestimonials };
  }
  
  const mapping = getLocaleMapping(locale.countryCode);
  
  if (!mapping) {
    return { testimonials: originalTestimonials };
  }
  
  const localized = originalTestimonials.map((t, idx) => localizeTestimonial(t, idx, mapping));
  
  return { testimonials: localized };
}

/**
 * Standalone function for use outside of React components (e.g., in config transforms)
 * Requires the locale countryCode to be passed explicitly.
 */
export function localizeTestimonials<T extends TestimonialInput>(
  testimonials: T[],
  countryCode: string
): T[] {
  if (countryCode === 'US' || !countryCode) {
    return testimonials;
  }
  
  const mapping = getLocaleMapping(countryCode);
  if (!mapping) return testimonials;
  
  return testimonials.map((t, idx) => localizeTestimonial(t, idx, mapping));
}

/**
 * Hook: useLocalizedLocation
 * 
 * Localizes a single location string (e.g., "Phoenix, AZ") for the current locale.
 * Uses a deterministic hash of the input string to pick from locale locations.
 * Useful for inline location references like "Phoenix, AZ · Verified Buyer".
 * 
 * @example
 * const localizedLocation = useLocalizedLocation("Phoenix, AZ");
 * // UK visitor sees: "Manchester, UK"
 * // US visitor sees: "Phoenix, AZ"
 */
export function useLocalizedLocation(usLocation: string): string {
  const { locale, isInternational } = useLocale();
  
  if (!isInternational) return usLocation;
  
  const mapping = getLocaleMapping(locale.countryCode);
  if (!mapping) return usLocation;
  
  const idx = simpleHash(usLocation) % mapping.locations.length;
  return mapping.locations[idx];
}

/**
 * Standalone version of localizeLocation for use outside React components.
 */
export function localizeLocation(usLocation: string, countryCode: string): string {
  if (countryCode === 'US' || !countryCode) return usLocation;
  
  const mapping = getLocaleMapping(countryCode);
  if (!mapping) return usLocation;
  
  const idx = simpleHash(usLocation) % mapping.locations.length;
  return mapping.locations[idx];
}
